import flattenColorPalette from 'tailwindcss/lib/util/flattenColorPalette'
import plugin from 'tailwindcss/plugin'
import { PluginAPI } from 'tailwindcss/types/config'

// Define types
type BorderSide = {
  property: string
  shadow: string
}

type BorderSides = {
  [key: string]: BorderSide
}

const defaultBoxShadow: string = [
  'var(--tw-ring-offset-shadow, 0 0 #0000)',
  'var(--tw-ring-shadow, 0 0 #0000)',
  'var(--tw-inner-border-shadow-top, 0 0 #0000)',
  'var(--tw-inner-border-shadow-right, 0 0 #0000)',
  'var(--tw-inner-border-shadow-bottom, 0 0 #0000)',
  'var(--tw-inner-border-shadow-left, 0 0 #0000)',
  'var(--tw-shadow, 0 0 #0000)',
].join(', ')

const innerBorderPlugin = plugin(
  ({
    addDefaults,
    matchUtilities,
    theme,
    corePlugins,
  }: PluginAPI & { addDefaults?: any }) => {
    // Add default for inner border color
    addDefaults('inner-border', {
      '--tw-inner-border-color': theme('borderColor.DEFAULT', 'currentColor'),
    })

    // All sides border - fixed
    matchUtilities(
      {
        'inner-border': (value: string) => ({
          '@defaults inner-border': {},
          '--tw-inner-border-width': value,
          '--tw-inner-border-shadow-top': `inset 0 var(--tw-inner-border-width) 0 0 var(--tw-inner-border-color)`,
          '--tw-inner-border-shadow-right': `inset calc(-1 * var(--tw-inner-border-width)) 0 0 0 var(--tw-inner-border-color)`,
          '--tw-inner-border-shadow-bottom': `inset 0 calc(-1 * var(--tw-inner-border-width)) 0 0 var(--tw-inner-border-color)`,
          '--tw-inner-border-shadow-left': `inset var(--tw-inner-border-width) 0 0 0 var(--tw-inner-border-color)`,
          'box-shadow': defaultBoxShadow,
        }),
      },
      {
        type: ['line-width', 'length'],
        values: theme('borderWidth'),
      },
    )

    // Individual sides - fixed
    const sides: BorderSides = {
      t: {
        property: 'top',
        shadow:
          'inset 0 var(--tw-inner-border-width-top) 0 0 var(--tw-inner-border-color)',
      },
      r: {
        property: 'right',
        shadow:
          'inset calc(-1 * var(--tw-inner-border-width-right)) 0 0 0 var(--tw-inner-border-color)',
      },
      b: {
        property: 'bottom',
        shadow:
          'inset 0 calc(-1 * var(--tw-inner-border-width-bottom)) 0 0 var(--tw-inner-border-color)',
      },
      l: {
        property: 'left',
        shadow:
          'inset var(--tw-inner-border-width-left) 0 0 0 var(--tw-inner-border-color)',
      },
    }

    Object.entries(sides).forEach(([key, { property, shadow }]) => {
      matchUtilities(
        {
          [`inner-border-${key}`]: (value: string) => ({
            '@defaults inner-border': {},
            [`--tw-inner-border-width-${property}`]: value,
            [`--tw-inner-border-shadow-${property}`]: shadow,
            'box-shadow': defaultBoxShadow,
          }),
        },
        {
          type: ['line-width', 'length'],
          values: theme('borderWidth'),
        },
      )
    })

    // Border color
    matchUtilities(
      {
        'inner-border': (value: string) => ({
          '--tw-inner-border-color': value,
        }),
      },
      {
        type: ['color', 'any'],
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        values: (({ DEFAULT: _, ...colors }: Record<string, any>) => colors)(
          flattenColorPalette(theme('borderColor')),
        ),
      },
    )

    /* Overrides to mitigate precedence issues */
    if (corePlugins('boxShadow')) {
      matchUtilities(
        { shadow: () => ({ 'box-shadow': defaultBoxShadow }) },
        { values: theme('boxShadow'), type: ['shadow'] },
      )
    }
    if (corePlugins('ringWidth')) {
      matchUtilities(
        { ring: () => ({ 'box-shadow': defaultBoxShadow }) },
        { values: theme('ringWidth'), type: 'length' },
      )
    }
  },
)

export default innerBorderPlugin
